import produce from 'immer';
import { createModel, RematchDispatch } from '@rematch/core';

import { RootModel } from '../models';
import { storeName as projectItemEntityStoreName } from '../entities/projectItem.entity';
import { storeName as facilityEntityStore } from '../entities/facility.entity';
import { buildHashUrl } from '../../utils';
import { DEFAULT_CONTENT_PANEL_DISPLAY_ITEM, SHARE_LINK_KEY } from '../../commons/constants';

export const storeName = 'amenitiesModel';

type AmenitiesModelTypes = {
  projectItemId: number;
  facilityKey: string;
  focusFacilityKey: string;
  isShowOverallInfoFacility: boolean;
  isShowSlider: boolean;
  groupFacilityKey: string;
  isAutoFocusImageMap: boolean;
  numberOfDisplayItem: number; // number of content panel display items
  isShowPanel: boolean;
};

const initialState: AmenitiesModelTypes = {
  projectItemId: null,
  facilityKey: null,
  groupFacilityKey: null,
  focusFacilityKey: null,
  isShowOverallInfoFacility: false,
  isShowSlider: true,
  isAutoFocusImageMap: false,
  numberOfDisplayItem: DEFAULT_CONTENT_PANEL_DISPLAY_ITEM,
  isShowPanel: true,
};

export const amenitiesModel: any = createModel<RootModel>()({
  name: storeName,
  state: initialState,
  reducers: {
    setIsShowSlider: produce((state, payload: boolean) => {
      state.isShowSlider = payload;
    }),
    setProjectItemId: produce((state, payload: number) => {
      if (!payload) {
        state.projectItemId = initialState.projectItemId;
        state.isShowSlider = initialState.isShowSlider;

        // reset state
        state.facilityKey = initialState.facilityKey;
        state.groupFacilityKey = initialState.groupFacilityKey;
        state.isShowOverallInfoFacility = initialState.isShowOverallInfoFacility;
        return;
      }

      state.projectItemId = payload;
      state.isShowSlider = initialState.isShowSlider;
      state.groupFacilityKey = initialState.groupFacilityKey;
    }),
    setFacilityDetail: produce((state, payload: string) => {
      if (!payload) {
        state.facilityKey = initialState.facilityKey;
        return;
      }

      state.facilityKey = payload;
      state.isShowSlider = initialState.isShowSlider;
    }),
    setFocusFacility: produce((state, payload: string) => {
      if (!payload) {
        state.focusFacilityKey = initialState.focusFacilityKey;
      } else {
        state.focusFacilityKey = payload;
      }
      state.isShowOverallInfoFacility = !!payload;
      state.isShowSlider = initialState.isShowSlider;
    }),
    setShowOverallInfoFacility: produce((state, payload: boolean) => {
      state.isShowOverallInfoFacility = payload;
    }),
    setGroupFacilityKey: produce((state, payload: string) => {
      state.groupFacilityKey = payload;
    }),
    setAutoFocusImageMap: produce((state, payload: boolean) => {
      state.isAutoFocusImageMap = payload;
    }),
    setConfig: produce((state, payload: { numberOfDisplayItem?: number }) => {
      if (payload.numberOfDisplayItem) state.numberOfDisplayItem = payload.numberOfDisplayItem;
    }),

    setShowPanel: produce((state, payload: boolean) => {
      state.isShowPanel = payload;
    }),
  },

  effects: (dispatch: RematchDispatch<any>) => ({
    updateHashParams(payload, state) {
      const {
        amenityKey = state[storeName].projectItemId,
        groupFacilityKey = state[storeName].groupFacilityKey,
        focusItemKey,
        itemKey,
      } = payload;

      // create new url params object
      const urlParams = {};
      if (amenityKey) {
        const projectItemEntityIds = state[projectItemEntityStoreName].ids;
        const amenityDetail = projectItemEntityIds && projectItemEntityIds[amenityKey];
        if (amenityDetail) {
          urlParams[SHARE_LINK_KEY.AMENITY] = amenityDetail.alias || amenityDetail.code;
        }
      }

      if (groupFacilityKey) {
        urlParams[SHARE_LINK_KEY.FACILITY_GROUP] = groupFacilityKey;
      }

      if (focusItemKey) {
        urlParams[SHARE_LINK_KEY.FACILITY_FOCUS_ITEM] = focusItemKey;
      }

      if (itemKey) {
        urlParams[SHARE_LINK_KEY.FACILITY] = itemKey;
      }

      buildHashUrl(urlParams);
    },
    async loadAmenityById(payload) {
      if (!payload) return;

      await dispatch[projectItemEntityStoreName].findById(payload);
    },
    loadFacilityByKey(payload) {
      if (!payload) return;

      dispatch[facilityEntityStore].loadByKey(payload);
    },
  }),
});

export default amenitiesModel;
